"use client";

import { useEffect, useState } from "react";
import type { Idioma } from "@/content/catalogo";
import { Img } from "@/components/Img";

type Foto = { archivo: string; alt: Record<Idioma, string> };
type Clip = { archivo: string; poster: string; alt: Record<Idioma, string> };
type Item = { tipo: "foto" | "video"; archivo: string; poster?: string; alt: Record<Idioma, string> };

export function GaleriaTour({ fotos: soloFotos, videos = [], idioma }: { fotos: Foto[]; videos?: Clip[]; idioma: Idioma }) {
  const fotos: Item[] = [...soloFotos.map((f): Item => ({ tipo: "foto", ...f })), ...videos.map((v): Item => ({ tipo: "video", ...v }))];
  const [abierta, setAbierta] = useState<number | null>(null);
  useEffect(() => {
    if (abierta === null) return;
    const tecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAbierta(null);
      if (e.key === "ArrowRight") setAbierta((i) => ((i ?? 0) + 1) % fotos.length);
      if (e.key === "ArrowLeft") setAbierta((i) => ((i ?? 0) + fotos.length - 1) % fotos.length);
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [abierta, fotos.length]);

  const es = idioma === "es";
  return <>
    <div className={`galeria galeria-interactiva${fotos.length === 1 ? " una" : ""}`}>
      {fotos.filter((f) => f.tipo === "foto").slice(0, 3).map((foto, i) => <button key={foto.archivo} type="button" data-mas={i === 2 && fotos.length > 3 ? `+${fotos.length - 3}` : undefined} onClick={() => setAbierta(i)} aria-label={`${es ? "Ver foto" : "View photo"}: ${foto.alt[idioma]}${fotos.length > 1 ? ` (${i + 1}/${fotos.length})` : ""}`}><Img src={foto.archivo} alt={foto.alt[idioma]} loading={i === 0 ? "eager" : "lazy"} /></button>)}
    </div>
    {abierta !== null && <div className="visor" role="dialog" aria-modal="true" aria-label={es ? "Fotos del tour" : "Tour photos"} onClick={() => setAbierta(null)}>
      <button type="button" className="visor-cerrar" onClick={() => setAbierta(null)} aria-label={es ? "Cerrar fotos" : "Close photos"}>×</button>
      <figure onClick={(e) => e.stopPropagation()}>
        {fotos[abierta].tipo === "video" ? <video key={fotos[abierta].archivo} src={fotos[abierta].archivo} poster={fotos[abierta].poster} autoPlay muted loop playsInline controls aria-label={fotos[abierta].alt[idioma]} /> : <Img src={fotos[abierta].archivo} alt={fotos[abierta].alt[idioma]} />}
        <figcaption>{fotos[abierta].alt[idioma]} · {abierta + 1}/{fotos.length}</figcaption>
      </figure>
      {fotos.length > 1 && <>
        <button type="button" className="visor-flecha izq" onClick={(e) => { e.stopPropagation(); setAbierta((abierta + fotos.length - 1) % fotos.length); }} aria-label={es ? "Foto anterior" : "Previous photo"}>‹</button>
        <button type="button" className="visor-flecha der" onClick={(e) => { e.stopPropagation(); setAbierta((abierta + 1) % fotos.length); }} aria-label={es ? "Foto siguiente" : "Next photo"}>›</button>
        <div className="visor-miniaturas">{fotos.map((foto, i) => <button key={foto.archivo} type="button" aria-pressed={abierta === i} onClick={(e) => { e.stopPropagation(); setAbierta(i); }}>{foto.tipo === "video" ? <Img src={foto.poster} alt="" /> : <Img src={foto.archivo} alt="" />}</button>)}</div>
      </>}
    </div>}
  </>;
}
